import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Box, Stack, Tabs, Tab, Typography } from '@mui/material';
import { ErrorMessage, Field, FieldArray, Formik } from 'formik';
import {
  useCreateLinkMutation,
  useUpdateLinkMutation,
  useGetLinkQuery,
  useGetToolsListQuery,
  useGetLocationsQuery,
} from 'api';
import {
  Breadcrumbs,
  Button,
  COLORS,
  Combobox,
  FormHelperText,
  TextField,
} from 'pvs-design-system';
import ComboboxOption from 'types/ComboboxOption';
import { ToolsQuery, Parameter } from 'types';
import { QueryStatus } from '@reduxjs/toolkit/query';
import * as Yup from 'yup';
import {
  PAGE_TITLE_HEADER_TEXT_ID,
  REG_EXP,
  ROUTES,
} from 'constants/constants';
import ErrorModal from 'components/ErrorModal/ErrorModal';
import Form from 'components/Form/Form';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import { Notify } from 'components/Notifications/Notifications';
import { CopyTextField } from 'components/CopyTextField/CopyTextField';
import bootConfig from 'utils/bootConfig';
import BuildUrl from 'utils/formatLink';
import ScrollToFieldError from 'components/ScrollToFieldError/ScrollToFieldError';
import MultipleLinksForm from './MultipleLinksForm';

type CustomParam = {
  key: string;
  name: string;
  value: string;
};

const VERTICAL_SPACING = 2;
const BUTTON_SPACING = 2;

const linkFormValidationSchema = Yup.object().shape({
  toolId: Yup.string().trim().required('Tool is required'),
  name: Yup.string()
    .trim()
    .required('Link Name is required')
    .max(50, 'Maximum of 50 characters'),
  id: Yup.string()
    .trim()
    .max(60, 'Maximum of 60 characters')
    .matches(
      REG_EXP.lettersNumbersDashes,
      'Link ID may only contain letters, numbers, and dashes. It may not contain spaces or any other special characters.'
    ),
  keywords: Yup.array()
    .of(Yup.string())
    .test((value, context) => {
      const keywordsLength = value.join(',').length;
      return keywordsLength > 255
        ? context.createError({ message: 'Maximum of 255 characters' })
        : true;
    }),
  queryParameters: Yup.array().of(
    Yup.object().shape({
      name: Yup.string(),
      value: Yup.string().trim().required('Parameter must have value'),
    })
  ),
  payloadParameters: Yup.array().of(
    Yup.object().shape({
      name: Yup.string(),
      value: Yup.string().trim().required('Parameter must have value'),
    })
  ),
  staticCustomParameters: Yup.array().of(
    Yup.object().shape({
      name: Yup.string(),
      value: Yup.string().trim().required('Parameter must have value'),
    })
  ),
  location: Yup.string().nullable(),
});

const SingleLinkForm: React.FC<{
  isEditMode: boolean;
  isReadonly: boolean;
}> = ({ isEditMode, isReadonly }) => {
  const { data: locationsData } = useGetLocationsQuery();
  const locations = locationsData || []; // Changed: locationsData is now directly a string array

  const { id } = useParams();
  const navigate = useNavigate();
  const [isDuplicateParamModalOpen, setIsDuplicateParamModalOpen] =
    useState(false);
  const [toolInputValue, setToolInputValue] = useState('');
  const [locationInputValue, setLocationInputValue] = useState('');
  const [selectedTab, setSelectedTab] = useState<
    'singleLink' | 'multipleLinks'
  >('singleLink');
  const buttonText = isEditMode ? 'Save Changes' : 'Add Link';

  const {
    data: link,
    isSuccess: isDataFetched,
    isError: isLinkError,
  } = useGetLinkQuery(id || '', {
    skip: !isEditMode,
  });

  const {
    data: tools,
    isLoading: isToolsLoading,
    isFetching: isToolsFetching,
    isError: isToolsError,
  } = useGetToolsListQuery({}) as ToolsQuery;

  const [showLoading, setShowLoading] = useState(false);

  useEffect(() => {
    setShowLoading(
      isToolsLoading || isToolsFetching || (!isDataFetched && isEditMode)
    );
  }, [isDataFetched, isEditMode, isToolsFetching, isToolsLoading, showLoading]);

  const [createLink, { status: createLinkStatus }] = useCreateLinkMutation();
  const [updateLink, { status: updateLinkStatus }] = useUpdateLinkMutation();
  const [keywords, setKeywords] = useState<string>('');
  const [initialValues, setInitialValues] = useState({
    id: '',
    name: '',
    toolId: null,
    keywords: [],
    queryParameters: [],
    payloadParameters: [],
    staticCustomParameters: [],
    location: null,
  });

  const failureMessage = `Link could not be ${
    isEditMode ? 'updated' : 'created'
  }. Please try again.`;

  const mapParamsForRequest = (parameters: CustomParam[]): Parameter[] => {
    return parameters.map((param) => {
      return {
        parameterName: param.name,
        parameterValue: param.value,
      };
    });
  };

  const onSubmit = async (values, { resetForm, setFieldError }) => {
    const linkPayload = {
      id: values.id,
      name: values.name,
      location: values.location,
      configuration: {
        toolId: values.toolId,
        keywords: values.keywords.map((keyword: string) => keyword.trim()),
        queryParameters: mapParamsForRequest(values.queryParameters),
        payloadParameters: mapParamsForRequest(values.payloadParameters),
        staticCustomParameters: mapParamsForRequest(
          values.staticCustomParameters
        ),
      },
    };

    try {
      if (isEditMode) {
        await updateLink(linkPayload).unwrap();
      } else {
        await createLink(linkPayload).unwrap();
      }
      resetForm();
      Notify.success(
        `Success, link has been ${isEditMode ? 'updated' : 'added'}.`
      );
      navigate(ROUTES.links);
    } catch (error) {
      if (error && error.status === 409) {
        if (error.data?.errors.startsWith('Duplicate Link Name')) {
          setFieldError('name', `${error.data.errors}.`);
        } else if (error.data?.errors.startsWith('Duplicate Link ID')) {
          setFieldError('id', `${error.data.errors}.`);
        } else if (
          error.data?.errors.startsWith(
            'Duplicate found for the given parameter combination values'
          )
        ) {
          setIsDuplicateParamModalOpen(true);
        } else {
          Notify.error(failureMessage);
        }
      } else {
        Notify.error(failureMessage);
      }
    }
  };

  const mapParamsFromLink = (parameters: Parameter[]): CustomParam[] => {
    return parameters.map((param) => {
      return {
        key: crypto.randomUUID(),
        name: param.parameterName,
        value: param.parameterValue,
      };
    });
  };

  // Load data into form
  useEffect(() => {
    if (!isEditMode) {
      return;
    }

    if (isLinkError) {
      navigate(ROUTES.createLink);
      return;
    }

    if (isDataFetched) {
      if (!link) {
        // eslint-disable-next-line no-console
        console.warn('Link data invalid');
        navigate(ROUTES.createLink);
        return;
      }

      setKeywords(link.configuration.keywords.join(', '));

      setInitialValues({
        id: link.id,
        name: link.name,
        toolId: link.configuration.toolId,
        keywords: link.configuration.keywords,
        queryParameters: mapParamsFromLink(
          link.configuration?.queryParameters || []
        ),
        payloadParameters: mapParamsFromLink(
          link.configuration?.payloadParameters || []
        ),
        staticCustomParameters: mapParamsFromLink(
          link.configuration?.staticCustomParameters || []
        ),
        location: link.location ?? null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link, isEditMode, navigate, isDataFetched, isLinkError]);

  const mapParamsFromTool = (
    params: Array<
      { parameterName: string; parameterValue: string } | string
    > = []
  ): CustomParam[] => {
    // If params are objects with parameterName/parameterValue
    if (
      params.length > 0 &&
      typeof params[0] === 'object' &&
      'parameterName' in params[0]
    ) {
      return (
        params as Array<{ parameterName: string; parameterValue: string }>
      ).map((param) => ({
        key: crypto.randomUUID(),
        name: param.parameterName,
        value: param.parameterValue,
      }));
    }
    // If params are strings, treat them as names and set value to empty
    return (params as string[]).map((paramName) => ({
      key: crypto.randomUUID(),
      name: paramName,
      value: '',
    }));
  };

  const onToolIdChange = (
    newValue: ComboboxOption,
    setFieldValue: (field: string, value: unknown) => void
  ) => {
    const selectedTool = tools.find((tool) => tool.id === newValue.value);

    setFieldValue('toolId', newValue.value);
    setFieldValue(
      'queryParameters',
      mapParamsFromTool(selectedTool?.dynamicParameter?.queryParameter) || []
    );
    setFieldValue(
      'payloadParameters',
      mapParamsFromTool(selectedTool?.dynamicParameter?.payloadParameter) || []
    );
    setFieldValue(
      'staticCustomParameters',
      mapParamsFromTool(
        selectedTool?.dynamicParameter?.staticCustomParameter
      ) || []
    );
  };

  const buildPageTitle = () => {
    if (isReadonly) {
      return `View Link`;
    }
    if (isEditMode) {
      return `Edit Link`;
    }
    return 'Add New Link';
  };

  return (
    <>
      <LoadingSpinner
        displayText={
          !isDataFetched && isEditMode
            ? 'Loading LTI 1.3 Link'
            : (isToolsLoading || isToolsFetching) && 'Loading LTI 1.3 Tools'
        }
        showLoading={showLoading}
      />

      {!isToolsError && !isToolsLoading && !isToolsFetching && tools && (
        <>
          <Breadcrumbs
            links={[
              {
                to: ROUTES.links,
                isMobileLink: true,
                linkText: 'Link Manager',
                component: Link,
              },
              {
                to: ROUTES.links,
                linkText: 'Link Manager',
                component: Link,
              },
              {
                href: '',
                linkText: buildPageTitle(),
                component: Link,
              },
            ]}
          />
          <Typography
            id={PAGE_TITLE_HEADER_TEXT_ID}
            variant="h1"
            marginTop="40px"
            textAlign="left"
          >
            {buildPageTitle()}
          </Typography>

          {/* center container: aligns Tabs + form fields */}
          <Box sx={{ maxWidth: 980, margin: '0 auto', px: 2, mt: 2 }}>
            {/* Tabs */}
            {!isEditMode && !isReadonly && (
              <Box sx={{ borderBottom: 'none', borderColor: 'divider', mb: 2 }}>
                <style>
                  {`
                    .custom-tabs .MuiTab-root,
                    .custom-tabs .MuiTab-root *,
                    .custom-tabs .MuiButtonBase-root,
                    .custom-tabs .MuiButtonBase-root *,
                    .custom-tabs .MuiTabs-root,
                    .custom-tabs .MuiTabs-root * {
                      border-radius: 0 !important;
                      border-top-left-radius: 0 !important;
                      border-top-right-radius: 0 !important;
                      border-bottom-left-radius: 0 !important;
                      border-bottom-right-radius: 0 !important;
                    }
                  `}
                </style>
                <Tabs
                  value={selectedTab}
                  onChange={(e, v) => setSelectedTab(v)}
                  aria-label="link-tabs"
                  className="custom-tabs"
                  sx={{
                    minHeight: 'auto',
                    '& .MuiTabs-indicator': {
                      display: 'none',
                    },
                    '& .MuiTabs-flexContainer': {
                      borderBottom: 'none',
                    },
                    '& .MuiTab-root': {
                      borderRadius: '0px !important',
                      borderTopLeftRadius: '0px !important',
                      borderTopRightRadius: '0px !important',
                      borderBottomLeftRadius: '0px !important',
                      borderBottomRightRadius: '0px !important',
                    },
                    '& .MuiButtonBase-root': {
                      borderRadius: '0px !important',
                      borderTopLeftRadius: '0px !important',
                      borderTopRightRadius: '0px !important',
                      borderBottomLeftRadius: '0px !important',
                      borderBottomRightRadius: '0px !important',
                    },
                  }}
                >
                  <Tab
                    value="singleLink"
                    label="Single Link"
                    disableRipple
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      color: '#77717A',
                      minHeight: 'auto',
                      paddingBottom: '8px',
                      backgroundColor: 'transparent',
                      borderBottom: '1px solid #BFBBBF',
                      borderRadius: '0px !important',
                      borderTopLeftRadius: '0px !important',
                      borderTopRightRadius: '0px !important',
                      borderBottomLeftRadius: '0px !important',
                      borderBottomRightRadius: '0px !important',
                      '&.Mui-selected': {
                        color: '#BF0366',
                        borderBottom: '2px solid #BF0366',
                        backgroundColor: 'transparent',
                        borderRadius: '0px !important',
                        borderTopLeftRadius: '0px !important',
                        borderTopRightRadius: '0px !important',
                        borderBottomLeftRadius: '0px !important',
                        borderBottomRightRadius: '0px !important',
                      },
                      '&:hover': {
                        backgroundColor: 'transparent',
                        borderRadius: '0px !important',
                        borderTopLeftRadius: '0px !important',
                        borderTopRightRadius: '0px !important',
                        borderBottomLeftRadius: '0px !important',
                        borderBottomRightRadius: '0px !important',
                      },
                    }}
                  />

                  <Tab
                    value="multipleLinks"
                    label="Multiple Links"
                    disableRipple
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      color: '#77717A',
                      minHeight: 'auto',
                      paddingBottom: '8px',
                      backgroundColor: 'transparent',
                      borderBottom: '1px solid #BFBBBF',
                      borderRadius: '0px !important',
                      borderTopLeftRadius: '0px !important',
                      borderTopRightRadius: '0px !important',
                      borderBottomLeftRadius: '0px !important',
                      borderBottomRightRadius: '0px !important',
                      '&.Mui-selected': {
                        color: '#BF0366',
                        borderBottom: '2px solid #BF0366',
                        backgroundColor: 'transparent',
                        borderRadius: '0px !important',
                        borderTopLeftRadius: '0px !important',
                        borderTopRightRadius: '0px !important',
                        borderBottomLeftRadius: '0px !important',
                        borderBottomRightRadius: '0px !important',
                      },
                      '&:hover': {
                        backgroundColor: 'transparent',
                        borderRadius: '0px !important',
                        borderTopLeftRadius: '0px !important',
                        borderTopRightRadius: '0px !important',
                        borderBottomLeftRadius: '0px !important',
                        borderBottomRightRadius: '0px !important',
                      },
                    }}
                  />
                </Tabs>
              </Box>
            )}

            {selectedTab === 'singleLink' && (
              <Formik
                initialValues={initialValues}
                validationSchema={linkFormValidationSchema}
                onSubmit={onSubmit}
                enableReinitialize
              >
                {({
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  setFieldValue,
                  values,
                  errors,
                  touched,
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <ScrollToFieldError />
                    <Stack spacing={VERTICAL_SPACING}>
                      <Combobox
                        inputValue={toolInputValue}
                        value={values.toolId}
                        defaultValue={
                          tools.find((tool) => tool.id === values.toolId) || ''
                        }
                        label="Tool"
                        onChange={(event, newValue: ComboboxOption) =>
                          onToolIdChange(newValue, setFieldValue)
                        }
                        onInputChange={(event, newInputValue) =>
                          setToolInputValue(newInputValue)
                        }
                        options={tools.map((tool) => ({
                          label: tool.id,
                          value: tool.id,
                        }))}
                        size="medium"
                        textFieldProps={{
                          id: 'toolId',
                          error: Boolean(touched.toolId && errors.toolId),
                          errorMessage: 'Tool is required',
                          required: true,
                          placeholder: 'Choose tool vendor',
                        }}
                        isOptionEqualToValue={(
                          option: ComboboxOption,
                          value: string
                        ) => option.value === value}
                        readOnly={isReadonly}
                      />
                      <TextField
                        id="name"
                        label="Link Name"
                        required
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        size="medium"
                        fullWidth
                        error={Boolean(touched.name && errors.name)}
                        errorMessage={String(errors.name)}
                        readOnly={isReadonly}
                      />

                      {!isEditMode && (
                        <TextField
                          id="id"
                          label="Link ID"
                          value={values.id}
                          readOnly={isEditMode}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          size="medium"
                          fullWidth
                          helperText={
                            isEditMode
                              ? 'Link ID cannot be edited'
                              : 'Please enter a unique string containing only letters, number, or dashes. This Link ID is not case sensitive and will be passed into the link URL. If a link is not entered, a GUID will be randomly assigned.'
                          }
                          error={Boolean(touched.id && errors.id)}
                          errorMessage={String(errors.id)}
                        />
                      )}
                      <Combobox
                        inputValue={locationInputValue}
                        value={values.location}
                        defaultValue={
                          locations.includes(values.location)
                            ? values.location
                            : ''
                        }
                        label="Location"
                        onChange={(event, newValue: ComboboxOption) =>
                          setFieldValue('location', newValue?.value || null)
                        }
                        onInputChange={(event, newInputValue) =>
                          setLocationInputValue(newInputValue)
                        }
                        options={locations.map((location) => ({
                          label: location,
                          value: location,
                        }))}
                        size="medium"
                        textFieldProps={{
                          id: 'location',
                          placeholder: 'Choose location',
                        }}
                        isOptionEqualToValue={(
                          option: ComboboxOption,
                          value: string
                        ) => option.value === value}
                        readOnly={isReadonly}
                      />
                      {isEditMode && (
                        <CopyTextField
                          id="url"
                          aria-label="Link URL"
                          defaultValue={BuildUrl(
                            bootConfig.ltiHandlerServiceBaseUrl,
                            values.id
                          )}
                          helperText="The Link URL cannot be edited."
                          label="Link URL"
                          onChange={handleChange}
                          readonly
                          required={false}
                          tooltipTitle="Copy Link"
                        />
                      )}
                      <TextField
                        id="keywords"
                        label="Keywords"
                        value={keywords}
                        onChange={(event) => {
                          setKeywords(event.target.value);
                          setFieldValue(
                            'keywords',
                            event.target.value.split(',')
                          );
                        }}
                        onBlur={handleBlur}
                        size="medium"
                        fullWidth
                        rows="2"
                        helperText="Separate keywords with a comma. Ex: fruit, pencil, photo"
                        error={Boolean(touched.keywords && errors.keywords)}
                        errorMessage={String(errors.keywords)}
                        readOnly={isReadonly}
                      />

                      <FieldArray
                        name="customParameters"
                        render={() => (
                          <>
                            {values.staticCustomParameters.map(
                              (param: CustomParam, index: number) => (
                                <Box key={param.key}>
                                  <Field
                                    as={TextField}
                                    id={`staticCustomParameters.${index}.value`}
                                    name={`staticCustomParameters.${index}.value`}
                                    label={param.name}
                                    required
                                    readOnly={isReadonly}
                                    size="medium"
                                    fullWidth
                                    index={index}
                                    value={param.value}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={
                                      Boolean(
                                        touched.staticCustomParameters &&
                                          touched.staticCustomParameters[
                                            index
                                          ] &&
                                          touched.staticCustomParameters[index]
                                            .value &&
                                          errors.staticCustomParameters &&
                                          errors.staticCustomParameters[
                                            index
                                          ] &&
                                          errors.staticCustomParameters[index]
                                            .value
                                      ) || false
                                    }
                                    errorMessage={
                                      // eslint-disable-next-line prettier/prettier, react/jsx-wrap-multilines
                                      <ErrorMessage
                                        name={`staticCustomParameters.${index}.value`}
                                      />
                                    }
                                  />
                                </Box>
                              )
                            )}

                            {values.queryParameters.map(
                              (param: CustomParam, index: number) => (
                                <Box key={param.key}>
                                  <Field
                                    as={TextField}
                                    id={`queryParameters.${index}.value`}
                                    name={`queryParameters.${index}.value`}
                                    label={param.name}
                                    required
                                    readOnly={isReadonly}
                                    size="medium"
                                    fullWidth
                                    index={index}
                                    value={param.value}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={
                                      Boolean(
                                        touched.queryParameters &&
                                          touched.queryParameters[index] &&
                                          touched.queryParameters[index]
                                            .value &&
                                          errors.queryParameters &&
                                          errors.queryParameters[index] &&
                                          errors.queryParameters[index].value
                                      ) || false
                                    }
                                    errorMessage={
                                      // eslint-disable-next-line prettier/prettier, react/jsx-wrap-multilines
                                      <ErrorMessage
                                        name={`queryParameters.${index}.value`}
                                      />
                                    }
                                  />
                                </Box>
                              )
                            )}

                            {values.payloadParameters.map(
                              (param: CustomParam, index: number) => (
                                <Box key={param.key}>
                                  <Field
                                    as={TextField}
                                    id={`payloadParameters.${index}.value`}
                                    name={`payloadParameters.${index}.value`}
                                    label={param.name}
                                    required
                                    readOnly={isReadonly}
                                    size="medium"
                                    fullWidth
                                    index={index}
                                    value={param.value}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={
                                      Boolean(
                                        touched.payloadParameters &&
                                          touched.payloadParameters[index] &&
                                          touched.payloadParameters[index]
                                            .value &&
                                          errors.payloadParameters &&
                                          errors.payloadParameters[index] &&
                                          errors.payloadParameters[index].value
                                      ) || false
                                    }
                                    errorMessage={
                                      // eslint-disable-next-line prettier/prettier, react/jsx-wrap-multilines
                                      <ErrorMessage
                                        name={`payloadParameters.${index}.value`}
                                      />
                                    }
                                  />
                                </Box>
                              )
                            )}
                          </>
                        )}
                      />

                      <FormHelperText
                        id="requiredIdText"
                        helperText="*indicates a required field"
                        sx={{ color: COLORS.red, padding: '24px 0px 0px 0px' }}
                      />
                    </Stack>
                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      alignItems="flex-start"
                      spacing={BUTTON_SPACING}
                    >
                      {isReadonly && (
                        <Link to={ROUTES.links} id="ok-link">
                          <Button id="readonly-ok" color="primary">
                            Ok
                          </Button>
                        </Link>
                      )}
                      {!isReadonly && (
                        <>
                          <Link to={ROUTES.links} id="cancel-link">
                            <Button
                              id="cancel"
                              color="secondary"
                              onClick={() => navigate(ROUTES.links)}
                            >
                              Cancel
                            </Button>
                          </Link>
                          <Button
                            id="submit"
                            type="submit"
                            isLoading={
                              createLinkStatus === QueryStatus.pending ||
                              updateLinkStatus === QueryStatus.pending
                            }
                          >
                            {buttonText}
                          </Button>
                        </>
                      )}
                    </Stack>
                  </Form>
                )}
              </Formik>
            )}

            {selectedTab === 'multipleLinks' && (
              <MultipleLinksForm tools={tools} />
            )}
          </Box>

          <ErrorModal
            title="Duplicate Link"
            errorMessage={`The link cannot be added because it is a duplicate for the tool ${initialValues.toolId}. A link already exists with the given parameter values.`}
            infoMessage="Please enter unique parameter values and try again."
            isOpen={isDuplicateParamModalOpen}
            onClose={() => setIsDuplicateParamModalOpen(false)}
          />
        </>
      )}
    </>
  );
};

export default SingleLinkForm;
